import { InvoiceSection, matchIndexedChild, Severity } from '@insio/validation';
import type { ValidationIssue } from '../../../../types';

const SEVERITY_RANK: Record<string, number> = { error: 2, warning: 1 };

export interface ValidationMaps {
  lineIssuesMap: Map<number, ValidationIssue[]>;
  fieldIssuesMap: Map<string, ValidationIssue[]>;
  lineSeverityMap: Map<number, 'error' | 'warning'>;
  fieldWorstMap: Map<string, { hasError: boolean; issue: ValidationIssue | undefined }>;
}

/** Bucket `invoiceLines.<index>[.<field>]` validation issues into lookup maps for
 * per-line and per-field annotations. Issues outside the `invoiceLines.*` namespace
 * are ignored. */
export function buildValidationMaps(errors: readonly ValidationIssue[]): ValidationMaps {
  const lineIssuesMap = new Map<number, ValidationIssue[]>();
  const fieldIssuesMap = new Map<string, ValidationIssue[]>();
  const lineSeverityMap = new Map<number, 'error' | 'warning'>();
  const fieldWorstMap = new Map<
    string,
    { hasError: boolean; issue: ValidationIssue | undefined }
  >();

  for (const issue of errors) {
    const match = issue.path ? matchIndexedChild(issue.path, InvoiceSection.invoiceLines) : null;
    if (!match) continue;
    const { index: lineIndex, child: field } = match;

    const lineArr = lineIssuesMap.get(lineIndex);
    if (lineArr) lineArr.push(issue);
    else lineIssuesMap.set(lineIndex, [issue]);

    if (field) {
      const fieldKey = `${lineIndex}|${field}`;
      const fieldArr = fieldIssuesMap.get(fieldKey);
      if (fieldArr) fieldArr.push(issue);
      else fieldIssuesMap.set(fieldKey, [issue]);
    }

    const { severity } = issue;
    const currentLineSev = lineSeverityMap.get(lineIndex);
    if (
      severity !== Severity.info &&
      (!currentLineSev || SEVERITY_RANK[severity] > SEVERITY_RANK[currentLineSev])
    ) {
      lineSeverityMap.set(lineIndex, severity);
    }
  }

  for (const [key, issues] of fieldIssuesMap) {
    let hasError = false;
    let bestIssue: ValidationIssue | undefined;
    let bestRank = -1;
    for (const issue of issues) {
      if (issue.severity === 'error') hasError = true;
      const rank = SEVERITY_RANK[issue.severity] ?? 0;
      if (rank > bestRank) {
        bestRank = rank;
        bestIssue = issue;
      }
    }
    fieldWorstMap.set(key, { hasError, issue: bestIssue });
  }

  return { lineIssuesMap, fieldIssuesMap, lineSeverityMap, fieldWorstMap };
}

/** True when an issue's path targets the line itself (e.g. `invoiceLines.0`),
 * not a specific field (e.g. `invoiceLines.0.unitPrice`). */
export function isLineLevelIssue(issue: ValidationIssue): boolean {
  const match = issue.path ? matchIndexedChild(issue.path, InvoiceSection.invoiceLines) : null;
  return match !== null && match.child === '';
}
